import { BookOpenIcon, ChartBarIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { ActionIcon, Badge, Box, Collapse, Group, Loader, Paper, Stack, Text, Tooltip, UnstyledButton, useMantineColorScheme } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import { getProfileCompletionPanelOpen, setProfileCompletionPanelOpen } from '../../../services/profileCompletionPanelStateService';
import { useProfileCompletion } from '../hooks/useProfileCompletion';
import { getScoreColor } from '../utils/scoreUtils';
import { CircularProgress } from './CircularProgress';
import { SectionCard } from './SectionCard';

interface ProfileCompletionPanelProps {
    personId: number | null | undefined;
    /** Pass Date.now() whenever a CV section is saved. The panel will refetch
     *  completion data after a short delay to let the backend calculation finish. */
    lastEditedAt?: number;
}

export const ProfileCompletionPanel = ({ personId, lastEditedAt }: ProfileCompletionPanelProps) => {
    const [open, setOpen] = useState(getProfileCompletionPanelOpen);
    const { data, isLoading, refetchAfterDelay } = useProfileCompletion(personId);

    const handleToggle = () => {
        setOpen((prev) => {
            const next = !prev;
            setProfileCompletionPanelOpen(next);
            return next;
        });
    };

    useEffect(() => {
        if (!lastEditedAt) return;
        refetchAfterDelay(2500);
    }, [lastEditedAt]);
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const { t } = useTranslation();

    if (!personId) {
        return null;
    }

    const pct = data ? Math.round(data.finalScore * 100) : null;
    const color = data ? getScoreColor(data.finalScore) : null;
    const issueCount = data
        ? data.sections.reduce((acc, s) => acc + s.rules.filter((r) => !r.passed).length, 0)
        : 0;

    const formattedDate = data
        ? new Date(data.calculatedAt).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
        })
        : '';

    return (
        <Box>
            {/* Pill trigger — left-aligned — + FAQ link */}
            <Group gap={6} align="center" mb="sm">
                <UnstyledButton onClick={handleToggle} disabled={isLoading || !data}>
                    <Group
                        gap={8}
                        px={18}
                        py={8}
                        style={{
                            background: color ? color.brandColor : 'var(--mantine-color-gray-5)',
                            borderRadius: 99,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            cursor: isLoading || !data ? 'default' : 'pointer',
                            transition: 'opacity 0.15s',
                            opacity: isLoading ? 0.7 : 1,
                        }}
                    >
                        {isLoading
                            ? <Loader size={14} color="white" />
                            : <ChartBarIcon style={{ width: 15, height: 15, color: 'white' }} />
                        }
                        <Text size="sm" fw={700} c="white">
                            {isLoading
                                ? t('PROFILE_COMPLETION_LOADING')
                                : pct !== null
                                    ? t('PROFILE_COMPLETION_TRIGGER_LABEL', { score: String(pct) })
                                    : t('PROFILE_COMPLETION_UNAVAILABLE')
                            }
                        </Text>
                        {!isLoading && data && (
                            <ChevronDownIcon style={{
                                width: 13,
                                height: 13,
                                color: 'white',
                                transition: 'transform 0.2s ease',
                                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                            }} />
                        )}
                    </Group>
                </UnstyledButton>

                <Tooltip label="Read more in FAQ" position="right" withArrow>
                    <ActionIcon
                        component="a"
                        href="/faq/profile-completion"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="subtle"
                        color="brand"
                        aria-label="Read more in FAQ"
                    >
                        <BookOpenIcon style={{ width: 18, height: 18 }} />
                    </ActionIcon>
                </Tooltip>
            </Group>

            {/* Full panel — capped width, left-anchored */}
            <Collapse in={open}>
                <Paper
                    radius="md"
                    withBorder={false}
                    style={{
                        overflow: 'hidden',
                        boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
                        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                    }}
                >
                    {/* Brand gradient header — compact */}
                    <Box
                        p="md"
                        style={{
                            background: 'linear-gradient(135deg, var(--mantine-color-brand-8) 0%, var(--mantine-color-brand-6) 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 14,
                        }}
                    >
                        {isLoading && (
                            <Box p="xl" style={{ display: 'flex', justifyContent: 'center' }}>
                                <Loader size="sm" color="white" />
                            </Box>
                        )}
                        {!isLoading && data && (
                            <>
                                <CircularProgress score={data.finalScore} size={80} strokeWidth={7} onDark />
                                <Stack gap={3} style={{ flex: 1 }}>
                                    <Text fw={700} size="md" c="white" style={{ lineHeight: 1.2 }}>
                                        {t('PROFILE_COMPLETION_TITLE')}
                                    </Text>
                                    <Text size="xs" style={{ lineHeight: 1.4, color: 'rgba(255,255,255,0.85)' }}>
                                        {t('PROFILE_COMPLETION_SUBTITLE')}
                                    </Text>
                                    {issueCount > 0 ? (
                                        <Badge
                                            mt={4}
                                            size="sm"
                                            radius="xl"
                                            style={{ background: 'rgba(251,191,36,0.18)', color: '#fde68a', border: 'none', width: 'fit-content' }}
                                        >
                                            {t('PROFILE_COMPLETION_IMPROVEMENTS_LABEL', { count: String(issueCount) })}
                                        </Badge>
                                    ) : (
                                        <Badge
                                            mt={4}
                                            size="sm"
                                            radius="xl"
                                            style={{ background: 'rgba(16,185,129,0.18)', color: '#6ee7b7', border: 'none', width: 'fit-content' }}
                                        >
                                            ✓ {t('PROFILE_COMPLETION_ALL_COMPLETE')}
                                        </Badge>
                                    )}
                                </Stack>
                            </>
                        )}
                    </Box>

                    {/* Section cards with full rule details */}
                    {data && (
                        <Stack gap={8} p={16}>
                            {data.sections.map((section) => (
                                <SectionCard key={section.sectionId} section={section} />
                            ))}
                        </Stack>
                    )}

                    {/* Footer */}
                    {data && (
                        <Group
                            px="md"
                            py="xs"
                            justify="space-between"
                            style={{
                                background: isDark ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-0)',
                                borderTop: `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}`,
                            }}
                        >
                            <Text size="xs" c="dimmed">
                                {t('PROFILE_COMPLETION_UPDATED_AT', { date: formattedDate })}
                            </Text>
                            <Text size="xs" c="dimmed">
                                {t('PROFILE_COMPLETION_REFRESH_NOTE')}
                            </Text>
                        </Group>
                    )}
                </Paper>
            </Collapse>
        </Box>
    );
};


